import { useEffect, useMemo, useRef, useState } from "react";
import {
  UploadSimple,
  CircleNotch,
  FilePdf,
  Image as ImageIcon,
  FilmSlate,
  Trash,
  Copy,
  Check,
  X,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { listMedia, deleteMedia, uploadFile } from "@/lib/api";

const KINDS = [
  { key: "all",   label: "Tutti",     icon: null },
  { key: "image", label: "Immagini",  icon: ImageIcon },
  { key: "pdf",   label: "PDF",       icon: FilePdf },
  { key: "video", label: "Video",     icon: FilmSlate },
];

const ACCEPT_ALL =
  "image/jpeg,image/png,image/webp,application/pdf,video/mp4,video/webm,video/quicktime";

function formatBytes(bytes) {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function MediaPanel() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const inputRef = useRef(null);

  const load = () => {
    setLoading(true);
    setError("");
    listMedia()
      .then((r) => {
        setItems(r.items || []);
        setCounts(r.counts || {});
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((i) => i.kind === filter)),
    [items, filter]
  );

  const totalCount = items.length;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploadError("");
    setUploading(true);
    const errors = [];
    for (const file of files) {
      try {
        await uploadFile(file);
      } catch (e) {
        errors.push(`${file.name}: ${e.message || "errore"}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    if (errors.length > 0) setUploadError(errors.join(" · "));
    load();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteMedia(deleteTarget.id);
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setCounts((prev) => ({
        ...prev,
        [deleteTarget.kind]: Math.max(0, (prev[deleteTarget.kind] || 1) - 1),
      }));
      setDeleteTarget(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const copyUrl = async (item) => {
    const absolute = new URL(item.url, window.location.origin).toString();
    try {
      await navigator.clipboard.writeText(absolute);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId((v) => (v === item.id ? null : v)), 1500);
    } catch {
      // fallback: seleziona il testo — se anche questo fallisce, nulla da fare.
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Libreria media</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter font-medium">
            {totalCount}{" "}
            <span className="text-[#5C5A55] font-light">
              {totalCount === 1 ? "file" : "file"}
            </span>
          </h2>
        </div>

        <div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="media-upload-btn"
            className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-[#EB0D0D] text-[#FAF9F6] hover:bg-[#C00A0A] transition-colors inline-flex items-center gap-2 disabled:opacity-50"
          >
            {uploading ? (
              <CircleNotch size={14} weight="bold" className="animate-spin" />
            ) : (
              <UploadSimple size={14} weight="bold" />
            )}
            {uploading ? "Caricamento…" : "Carica file"}
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT_ALL}
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            className="hidden"
          />
        </div>
      </div>

      {/* Filtri per tipo */}
      <div className="flex flex-wrap gap-2">
        {KINDS.map((k) => {
          const Icon = k.icon;
          const active = filter === k.key;
          const count =
            k.key === "all"
              ? totalCount
              : counts[k.key] || 0;
          return (
            <button
              key={k.key}
              type="button"
              onClick={() => setFilter(k.key)}
              data-testid={`media-filter-${k.key}`}
              className={`px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold border transition-colors inline-flex items-center gap-2 ${
                active
                  ? "bg-[#1A1918] text-[#FAF9F6] border-[#1A1918]"
                  : "bg-transparent text-[#1A1918] border-[#D1CFC7] hover:border-[#1A1918]"
              }`}
            >
              {Icon && <Icon size={14} weight="bold" />}
              {k.label}
              <span
                className={`ml-1 px-1.5 py-0.5 text-[10px] ${
                  active ? "bg-[#FAF9F6]/20" : "bg-[#F0EFEB] text-[#5C5A55]"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {uploadError && (
        <div className="p-3 bg-[#EB0D0D]/8 text-[#C00A0A] text-sm border border-[#EB0D0D]/30">
          {uploadError}
        </div>
      )}

      {error && (
        <div className="p-3 bg-[#EB0D0D]/8 text-[#C00A0A] text-sm border border-[#EB0D0D]/30">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <CircleNotch
            size={28}
            weight="bold"
            className="animate-spin text-[#EB0D0D]"
          />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#F0EFEB] border border-[#D1CFC7]/40 py-16 text-center text-[#5C5A55]">
          {totalCount === 0
            ? "Nessun file caricato. Usa il pulsante «Carica file» per iniziare."
            : "Nessun file con questo filtro."}
        </div>
      ) : (
        <div
          data-testid="media-grid"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
        >
          {filtered.map((m) => (
            <MediaCard
              key={m.id}
              item={m}
              onDelete={() => setDeleteTarget(m)}
              onCopy={() => copyUrl(m)}
              copied={copiedId === m.id}
            />
          ))}
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#1A1918]/60 backdrop-blur-sm"
          onClick={() => (busyId ? null : setDeleteTarget(null))}
        >
          <div
            className="bg-[#FAF9F6] w-full max-w-md border border-[#D1CFC7] shadow-2xl p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="media-delete-modal"
          >
            <div className="overline mb-3">Conferma eliminazione</div>
            <h3 className="font-display text-2xl tracking-tighter font-medium mb-3">
              Eliminare{" "}
              <em className="italic font-light text-[#EB0D0D] break-all">
                {deleteTarget.orig_name || deleteTarget.filename}
              </em>
              ?
            </h3>
            <p className="text-sm text-[#5C5A55] mb-6">
              Il file verrà rimosso dal server e dal registro. Se è ancora
              collegato a un prodotto o premio, quel riferimento si spezzerà.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={!!busyId}
                className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-transparent text-[#1A1918] border border-[#D1CFC7] hover:border-[#1A1918] transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={confirmDelete}
                disabled={!!busyId}
                data-testid="media-delete-confirm-btn"
                className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-[#EB0D0D] text-[#FAF9F6] hover:bg-[#C00A0A] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              >
                {busyId ? (
                  <CircleNotch size={14} weight="bold" className="animate-spin" />
                ) : (
                  <Trash size={14} weight="bold" />
                )}
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MediaCard({ item, onDelete, onCopy, copied }) {
  const isImage = item.kind === "image";
  const isVideo = item.kind === "video";
  const isPdf = item.kind === "pdf";
  const displayName = item.orig_name || item.filename;

  return (
    <div
      data-testid={`media-item-${item.id}`}
      className="group bg-[#F0EFEB] border border-[#D1CFC7]/40 hover:border-[#1A1918] transition-colors flex flex-col"
    >
      <div className="relative aspect-square bg-[#FAF9F6] flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img
            src={item.url}
            alt={displayName}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : isVideo ? (
          <>
            <video
              src={item.url}
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-[#1A1918]/30 pointer-events-none">
              <FilmSlate size={40} weight="fill" className="text-[#FAF9F6]" />
            </div>
          </>
        ) : (
          <FilePdf size={56} weight="duotone" className="text-[#EB0D0D]" />
        )}

        <div className="absolute top-2 left-2">
          <span className="px-2 py-1 bg-[#1A1918]/80 text-[#FAF9F6] text-[10px] uppercase tracking-[0.15em] font-bold">
            {isImage ? "IMG" : isPdf ? "PDF" : "VIDEO"}
          </span>
        </div>

        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 bg-[#FAF9F6]/95 hover:bg-[#FAF9F6] text-[#1A1918]"
            title="Apri in nuova scheda"
            onClick={(e) => e.stopPropagation()}
          >
            <ArrowSquareOut size={14} weight="bold" />
          </a>
          <button
            type="button"
            onClick={onCopy}
            className="p-1.5 bg-[#FAF9F6]/95 hover:bg-[#FAF9F6] text-[#1A1918]"
            title="Copia URL"
          >
            {copied ? (
              <Check size={14} weight="bold" className="text-[#0E8A3E]" />
            ) : (
              <Copy size={14} weight="bold" />
            )}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 bg-[#FAF9F6]/95 hover:bg-[#EB0D0D] hover:text-[#FAF9F6] text-[#EB0D0D]"
            title="Elimina"
            data-testid={`media-delete-${item.id}`}
          >
            <Trash size={14} weight="bold" />
          </button>
        </div>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-1">
        <div
          className="text-xs text-[#1A1918] truncate font-medium"
          title={displayName}
        >
          {displayName}
        </div>
        <div className="flex items-center justify-between text-[10px] text-[#5C5A55]">
          <span>{formatBytes(item.size_bytes)}</span>
          <span className="truncate ml-2">{formatDate(item.created_at)}</span>
        </div>
      </div>
    </div>
  );
}
